import { InputModel } from "@husky-hrc/Framework/Types/ApiModel";
import Constants from "@husky-hrc/Utilities/Constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Info from "@insite/mobius/Icons/Info";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal/Modal";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { css } from "styled-components";

export interface TooltipStyles {
    modal?: ModalPresentationProps;
    closeModalButton?: ButtonPresentationProps;
}

const tooltipStyles: TooltipStyles = {
    modal: Constants.Styles.modalStyles.modal,
    closeModalButton: Constants.Styles.modalStyles.cancelButton,
};

const TooltipWrapper = styled.div`
    position: relative;
    flex-grow: 1;
    margin-left: 6px;
    display: flex;
    &:hover {
        span {
            color: ${getColor("danger.main")};
        }
    }

    > div:focus {
        outline: 2px solid ${getProp("theme.focus.color")};
    }

    span {
        svg {
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 21px;
                            width: 21px;
                            stroke-width: 1;
                        `,
                        null,
                        null,
                        css`
                            height: 16px;
                            width: 16px;
                            stroke-width: 2;
                        `,
                    ],
                    "min",
                )}
        }
    }
`;

const TooltipText = styled.div`
    position: absolute;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.25);
    font-size: 12px;
    font-weight: normal;
    padding: 15px;
    line-height: 18px;
    letter-spacing: 0.7px;
    box-shadow: 0 0 7px 0 rgba(0, 0, 0, 0.25);
    border-radius: 2px;
    top: 28px;
    left: -15px;
    white-space: pre-wrap;
    min-width: 250px;
    z-index: 1000;
    &:before {
        content: "";
        position: absolute;
        width: 0;
        height: 0;
        border-left: 7px solid transparent;
        border-right: 7px solid transparent;
        border-bottom: 7px solid rgba(0, 0, 0, 0.25);
        top: -8px;
        left: 15px;
    }
    &:after {
        content: "";
        display: block;
        position: absolute;
        width: 0;
        height: 0;
        border-left: 7px solid transparent;
        border-right: 7px solid transparent;
        border-bottom: 7px solid #fff;
        top: -7px;
        left: 15px;
    }
`;

const styles = tooltipStyles;

interface OwnProps {
    input: InputModel;
}

type Props = OwnProps;

const HuskyTooltip = ({ input }: Props) => {
    const buttonStyles = {
        ...styles.closeModalButton,
        css: css`
            ${styles.closeModalButton?.css || ""};
            display: block;
            margin: 30px auto 0;
        `,
    };
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [showTooltipModal, setShowTooltipModal] = React.useState(false);

    const onKeyDownHandler = (e: React.KeyboardEvent, type: string) => {
        const key = e.nativeEvent.code.toLowerCase();
        if (key === "enter" || key === "space") {
            e.preventDefault();
            if (type === "tooltip") {
                setShowTooltip(!showTooltip);
            } else {
                setShowTooltipModal(!showTooltipModal);
            }
        }
    };

    return (
        <>
            <Hidden below="lg">
                <TooltipWrapper>
                    <StyledWrapper
                        role="button"
                        tabIndex={0}
                        onMouseOver={() => setShowTooltip(true)}
                        onMouseOut={() => setShowTooltip(false)}
                        onClick={() => setShowTooltip(!showTooltip)}
                        onKeyDown={e => onKeyDownHandler(e, "tooltip")}
                        onBlur={() => setShowTooltip(false)}
                    >
                        <Icon color="#727578" src={Info} />
                        <VisuallyHidden>{`${translate("More info on")} ${input.description}`}</VisuallyHidden>
                    </StyledWrapper>
                    <div aria-live="polite">{showTooltip && <TooltipText>{input.tooltip}</TooltipText>}</div>
                </TooltipWrapper>
            </Hidden>
            <Hidden above="md">
                <TooltipWrapper
                    tabIndex={0}
                    role="button"
                    onClick={() => setShowTooltipModal(!showTooltipModal)}
                    onKeyDown={e => onKeyDownHandler(e, "modal")}
                >
                    <Icon size={16} color="#727578" src={Info} />
                    <VisuallyHidden>{`${translate("More info on")} ${input.description}`}</VisuallyHidden>
                </TooltipWrapper>
                <Modal
                    handleClose={() => setShowTooltipModal(false)}
                    headline={input.description}
                    isCloseable={true}
                    isOpen={showTooltipModal}
                    {...styles.modal}
                >
                    {input.tooltip}
                    <Button
                        {...buttonStyles}
                        onKeyDown={e => onKeyDownHandler(e, "modal")}
                        onClick={() => setShowTooltipModal(false)}
                    >
                        {translate("Close")}
                    </Button>
                </Modal>
            </Hidden>
        </>
    );
};

export default HuskyTooltip;
